/**
 * DIEUKYDIEU.TV - SIDEBAR DYNAMIC TREE RENDERER
 */
import { t } from '../i18n.js';
import { state } from '../state.js';
import { getSectionsData, getSitemapTree, REFERENCE_NETWORK_LINKS } from '../explore.js';
import { mediaCategories } from '../media-data.js';

export function renderSidebarDynamicMenus() {
    // 1. Explore Category Tree (#grp-explore .sidebar-group-content)
    const exploreContent = document.querySelector('#grp-explore .sidebar-group-content');
    if (exploreContent) {
        const tree = getSitemapTree();
        const sections = getSectionsData();
        const sourceList = (Array.isArray(tree) && tree.length > 0) ? tree : sections;
        const excludedSlugs = new Set(['bai-viet-cua-dai-su-ly-hong-chi', 'video', 'audio-nhac', 'lien-ket-tham-khao', 'media']);

        const exploreCats = sourceList.filter(cat => {
            const slug = cat.slug || cat.id || '';
            return slug && !excludedSlugs.has(slug);
        });

        // Preserve currently expanded tree IDs before re-render
        const expandedIds = new Set(
            Array.from(exploreContent.querySelectorAll('.sidebar-tree-container.expanded')).map(el => el.id)
        );

        exploreContent.innerHTML = exploreCats.map(cat => {
            const slug = cat.slug || cat.id;
            const treeId = `tree-${slug}`;
            const isExpanded = expandedIds.has(treeId) ? ' expanded' : '';
            const catLabel = t('sec_' + slug) || cat.name;
            const children = Array.isArray(cat.children) ? cat.children : [];
            const catIcon = cat.icon || 'auto_awesome';

            const subItemsHtml = children.map(sub => {
                const subId = sub.id || sub.name;
                return `
                    <button class="sidebar-sub-item" onclick="openSidebarCategory('${slug}', '${subId}')" data-category-sub="${subId}">
                        <span class="sidebar-sub-text">${sub.name}</span>
                    </button>
                `;
            }).join('');

            return `
                <div class="sidebar-tree-container${isExpanded}" id="${treeId}">
                    <div class="sidebar-tree-row">
                        <button class="sidebar-nav-item" onclick="openSidebarCategory('${slug}')" data-category-slug="${slug}" aria-label="${catLabel}">
                            <span class="material-symbols-rounded sidebar-item-icon">${catIcon}</span>
                            <span class="sidebar-nav-text">${catLabel}</span>
                        </button>
                        ${children.length > 0 ? `
                        <button class="tree-chevron-btn" onclick="toggleCategoryTree('${treeId}', event)" aria-label="Mở rộng mục con ${catLabel}" title="Mở rộng mục con">
                            <span class="material-symbols-rounded tree-chevron-icon">chevron_right</span>
                        </button>` : ''}
                    </div>
                    ${children.length > 0 ? `<div class="sidebar-sub-tree">${subItemsHtml}</div>` : ''}
                </div>
            `;
        }).join('');
    }

    // 2. Media Category Tree (#grp-media .sidebar-group-content)
    const mediaContent = document.querySelector('#grp-media .sidebar-group-content');
    if (mediaContent && mediaCategories) {
        const expandedMediaIds = new Set(
            Array.from(mediaContent.querySelectorAll('.sidebar-tree-container.expanded')).map(el => el.id)
        );
        const mediaEntries = Object.entries(mediaCategories).filter(([id]) => id !== 'all');

        mediaContent.innerHTML = mediaEntries.map(([mId, mCat]) => {
            const treeId = `tree-media-${mId}`;
            const isExpanded = expandedMediaIds.has(treeId) ? ' expanded' : '';
            const mLabel = t(`media_cat_${mId}`) || mCat.name;
            const subs = (Array.isArray(mCat.sub) ? mCat.sub : []).filter(s => s.id !== 'all');
            const mIcon = mCat.icon || 'smart_display';

            const subItemsHtml = subs.map(sub => `
                <button class="sidebar-sub-item" onclick="openSidebarMedia('${mId}', '${sub.id}')" data-media-sub="${sub.id}">
                    <span class="sidebar-sub-text">${sub.name}</span>
                </button>
            `).join('');

            return `
                <div class="sidebar-tree-container${isExpanded}" id="${treeId}">
                    <div class="sidebar-tree-row">
                        <button class="sidebar-nav-item" onclick="openSidebarMedia('${mId}', 'all')" data-media-type="${mId}" aria-label="${mLabel}">
                            <span class="material-symbols-rounded sidebar-item-icon">${mIcon}</span>
                            <span class="sidebar-nav-text">${mLabel}</span>
                        </button>
                        ${subs.length > 0 ? `
                        <button class="tree-chevron-btn" onclick="toggleCategoryTree('${treeId}', event)" aria-label="Mở rộng mục ${mLabel}" title="Mở rộng mục con">
                            <span class="material-symbols-rounded tree-chevron-icon">chevron_right</span>
                        </button>` : ''}
                    </div>
                    ${subs.length > 0 ? `<div class="sidebar-sub-tree">${subItemsHtml}</div>` : ''}
                </div>
            `;
        }).join('');
    }

    // 3. Reference Links (#grp-links .sidebar-group-content)
    const linksContent = document.querySelector('#grp-links .sidebar-group-content');
    if (linksContent && Array.isArray(REFERENCE_NETWORK_LINKS)) {
        linksContent.innerHTML = REFERENCE_NETWORK_LINKS.map(item => `
            <a href="${item.href}" target="_blank" rel="noopener noreferrer" class="sidebar-nav-item sidebar-ext-link" title="${item.name} - ${t(item.descKey) || item.defaultDesc}">
                <span class="material-symbols-rounded sidebar-item-icon">link</span>
                <span class="sidebar-nav-text">${item.sidebarName || item.name}</span>
                <span class="material-symbols-rounded ext-icon">open_in_new</span>
            </a>
        `).join('');
    }
}

/**
 * Synchronize Sidebar Active State (2-Level Intelligent Hierarchy)
 */
export function updateSidebarActive(viewId, categorySlug = null, mediaType = null, mediaSub = null, categorySub = null) {
    const isHome = (viewId === 'home-view');
    const isExploreHub = (viewId === 'category-view');
    const isCategoryDetail = (viewId === 'category-detail-view');
    const isMedia = (viewId === 'media-view');

    // 1. Single Top Home Pill
    const homeBtn = document.querySelector('#sidebar-nav-scroll > .sidebar-nav-item[data-nav-target="home-view"]');
    if (homeBtn) {
        homeBtn.classList.toggle('active', isHome);
        homeBtn.setAttribute('aria-selected', isHome ? 'true' : 'false');
    }

    // 2. Explore Group Header
    const grpExplore = document.getElementById('grp-explore');
    const exploreBtn = grpExplore ? grpExplore.querySelector('.sidebar-group-title-btn') : null;
    const exploreHeader = grpExplore ? grpExplore.querySelector('.sidebar-group-header') : null;

    if (exploreBtn) {
        exploreBtn.classList.toggle('active', isExploreHub);
        exploreBtn.classList.toggle('active-parent', isCategoryDetail);
        exploreBtn.setAttribute('aria-selected', (isExploreHub || isCategoryDetail) ? 'true' : 'false');
    }
    if (exploreHeader) {
        exploreHeader.classList.toggle('active-parent', isCategoryDetail);
    }

    // 3. Category Items inside Explore
    const allCatItems = document.querySelectorAll('#grp-explore [data-category-slug]');
    allCatItems.forEach(item => {
        const itemSlug = item.getAttribute('data-category-slug');
        const isCatActive = isCategoryDetail && categorySlug && (itemSlug === categorySlug);
        item.classList.toggle('active', !!isCatActive);
        item.setAttribute('aria-selected', isCatActive ? 'true' : 'false');
    });

    const allCatSubItems = document.querySelectorAll('#grp-explore [data-category-sub]');
    allCatSubItems.forEach(item => {
        const itemSub = item.getAttribute('data-category-sub');
        const isSubActive = isCategoryDetail && categorySub && (itemSub === categorySub);
        item.classList.toggle('active', !!isSubActive);
        item.setAttribute('aria-selected', isSubActive ? 'true' : 'false');
    });

    if (isCategoryDetail && categorySlug && categorySub) {
        const activeCatTree = document.getElementById(`tree-${categorySlug}`);
        if (activeCatTree) activeCatTree.classList.add('expanded');
    }

    if (isCategoryDetail && grpExplore && grpExplore.classList.contains('collapsed')) {
        grpExplore.classList.remove('collapsed');
    }

    // 4. Media Group Header & Nested Sub-Trees
    const grpMedia = document.getElementById('grp-media');
    const mediaBtn = grpMedia ? grpMedia.querySelector('.sidebar-group-title-btn') : null;
    if (mediaBtn) {
        mediaBtn.classList.toggle('active', isMedia && (!mediaType || mediaType === 'all'));
        mediaBtn.classList.toggle('active-parent', isMedia && !!mediaType && mediaType !== 'all');
        mediaBtn.setAttribute('aria-selected', isMedia ? 'true' : 'false');
    }

    const allMediaItems = document.querySelectorAll('#grp-media [data-media-type]');
    allMediaItems.forEach(item => {
        const itemType = item.getAttribute('data-media-type');
        const isTypeActive = isMedia && mediaType && (itemType === mediaType);
        item.classList.toggle('active', !!isTypeActive);
        item.setAttribute('aria-selected', isTypeActive ? 'true' : 'false');
    });

    const allMediaSubItems = document.querySelectorAll('#grp-media [data-media-sub]');
    allMediaSubItems.forEach(item => {
        const itemSub = item.getAttribute('data-media-sub');
        const isSubActive = isMedia && mediaSub && (itemSub === mediaSub);
        item.classList.toggle('active', !!isSubActive);
        item.setAttribute('aria-selected', isSubActive ? 'true' : 'false');
    });

    if (isMedia && mediaType && mediaType !== 'all') {
        const activeTree = document.getElementById(`tree-media-${mediaType}`);
        if (activeTree) activeTree.classList.add('expanded');
    }

    // 5. Links (Tham khảo) Group Header
    const isLinks = (viewId === 'links');
    const grpLinks = document.getElementById('grp-links');
    const linksBtn = grpLinks ? grpLinks.querySelector('.sidebar-group-title-btn') : null;
    if (linksBtn) {
        linksBtn.classList.toggle('active', isLinks);
        linksBtn.setAttribute('aria-selected', isLinks ? 'true' : 'false');
    }

    // 6. Chat & Hỗ trợ button
    const chatBtn = document.getElementById('sidebar-nav-chat-btn');
    if (chatBtn) {
        chatBtn.classList.toggle('active', !!state.isLiveChatOpen);
        chatBtn.setAttribute('aria-selected', state.isLiveChatOpen ? 'true' : 'false');
    }
}


